import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const PROTOCOLS = [
  "CALM", "SAVAGE", "RAMPED UP", "BI POLAR", "NORMAL", "HAPPY", "BLISS", "EUPHORIA",
  "LSD analog", "DMT analog", "Psilocybin analog", "Ketamine analog", "THC analog", "Alcohol analog"
];

export default function VisualsEnhanced(){
  const [preset, setPreset] = useState("bars");
  const [img, setImg] = useState("");
  const [audioSrc, setAudioSrc] = useState("");
  const [selectedProtocol, setSelectedProtocol] = useState("CALM");
  const [protocolVisuals, setProtocolVisuals] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [tdVideoFile, setTdVideoFile] = useState(null);
  const [tdStreamUrl, setTdStreamUrl] = useState("");
  
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    loadProtocolVisuals();
    const saved = localStorage.getItem("featuredItem");
    if(saved){ 
      try{ 
        const it = JSON.parse(saved); 
        setPreset(it.visual_preset||"bars"); 
        setImg(it.image_url||""); 
        setAudioSrc(it.audio_url||"");
      } catch(e) {
        console.error("Failed to parse featuredItem:", e);
      }
    }
  }, []);

  useEffect(() => {
    return () => { 
      if(rafRef.current) cancelAnimationFrame(rafRef.current); 
    };
  }, []);

  const loadProtocolVisuals = async () => {
    try {
      const res = await axios.get(`${API}/protocol-visuals`);
      const map = {};
      res.data.forEach(v => {
        map[v.protocol_name] = v;
      });
      setProtocolVisuals(map);
    } catch(e) {
      console.error("Failed to load protocol visuals", e);
    }
  };

  const uploadProtocolImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload-image`, formData);
      const imageUrl = res.data.url;

      const existing = protocolVisuals[selectedProtocol];
      if (existing) {
        const updated = {
          ...existing,
          image_urls: [...existing.image_urls, imageUrl]
        };
        await axios.put(`${API}/protocol-visuals/${existing.id}`, updated);
      } else {
        await axios.post(`${API}/protocol-visuals`, {
          protocol_name: selectedProtocol,
          image_urls: [imageUrl],
          td_video_url: null,
          td_stream_url: null
        });
      }
      
      toast.success("Image uploaded for " + selectedProtocol);
      loadProtocolVisuals();
    } catch(e) {
      toast.error("Upload failed");
      console.error(e);
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadTDVideo = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload-image`, formData);
      const videoUrl = res.data.url;

      const existing = protocolVisuals[selectedProtocol];
      if (existing) {
        const updated = {
          ...existing,
          td_video_url: videoUrl
        };
        await axios.put(`${API}/protocol-visuals/${existing.id}`, updated);
      } else {
        await axios.post(`${API}/protocol-visuals`, {
          protocol_name: selectedProtocol,
          image_urls: [],
          td_video_url: videoUrl,
          td_stream_url: null
        });
      }
      
      toast.success("Touch Designer video uploaded for " + selectedProtocol);
      loadProtocolVisuals();
    } catch(e) {
      toast.error("Upload failed");
      console.error(e);
    } finally {
      setUploadingImage(false);
    }
  };

  const saveTDStreamUrl = async () => {
    if (!tdStreamUrl) return;
    try {
      const existing = protocolVisuals[selectedProtocol];
      if (existing) {
        const updated = {
          ...existing,
          td_stream_url: tdStreamUrl
        };
        await axios.put(`${API}/protocol-visuals/${existing.id}`, updated);
      } else {
        await axios.post(`${API}/protocol-visuals`, {
          protocol_name: selectedProtocol,
          image_urls: [],
          td_video_url: null,
          td_stream_url: tdStreamUrl
        });
      }
      
      toast.success("TD stream URL saved for " + selectedProtocol);
      loadProtocolVisuals();
      setTdStreamUrl("");
    } catch(e) {
      toast.error("Save failed");
      console.error(e);
    }
  };

  const start = async () => {
    try {
      const audio = audioRef.current;
      if(!audio) return;
      await audio.play();
      const ac = new (window.AudioContext||window.webkitAudioContext)();
      const src = ac.createMediaElementSource(audio);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      analyser.connect(ac.destination);
      analyserRef.current = analyser;
      draw();
    } catch(e) {
      toast.error("Unable to start visuals");
    }
  };

  const stop = () => {
    if(rafRef.current) cancelAnimationFrame(rafRef.current);
    const a = audioRef.current;
    if(a) {
      a.pause();
      a.currentTime = 0;
    }
  };

  const draw = () => {
    const cvs = canvasRef.current;
    const ctx = cvs.getContext('2d');
    const analyser = analyserRef.current;
    if(!analyser) return;
    const W = cvs.width = cvs.clientWidth;
    const H = cvs.height = 480;
    const data = new Uint8Array(analyser.frequencyBinCount);
    
    const render = () => {
      analyser.getByteFrequencyData(data);
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0,0,W,H);
      
      if(img) {
        const im = new Image();
        im.src = img;
        ctx.globalAlpha=0.08;
        ctx.drawImage(im,0,0,W,H);
        ctx.globalAlpha=1;
      }
      
      if(preset === 'bars') {
        const n = 64;
        const step = Math.floor(data.length / n);
        for(let i=0;i<n;i++) {
          const v = data[i*step]/255;
          const h = v*H;
          ctx.fillStyle = i%2? "#a4ff2d" : "#ff1744";
          ctx.fillRect(i*(W/n)+2, H-h, (W/n)-4, h);
        }
      } else if(preset === 'wave') {
        const t = new Uint8Array(analyser.fftSize);
        analyser.getByteTimeDomainData(t);
        ctx.strokeStyle = "#a4ff2d";
        ctx.lineWidth=2;
        ctx.beginPath();
        for(let x=0;x<W;x++) {
          const idx = Math.floor(x/W * t.length);
          const y = (t[idx]/255)*H;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      } else {
        const n = 120;
        for(let i=0;i<n;i++) {
          const v = data[(i*3)%data.length]/255;
          const x = (i/n)*W + Math.sin(v*6.28 + i)*8;
          const y = H*(1-v);
          ctx.fillStyle = `rgba(255,23,68,${0.4+0.6*v})`;
          ctx.beginPath();
          ctx.arc(x,y,2+6*v,0,Math.PI*2);
          ctx.fill();
        }
      }
      rafRef.current = requestAnimationFrame(render);
    };
    render();
  };

  const currentVisual = protocolVisuals[selectedProtocol];

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 space-y-6">
      <h1 className="text-3xl font-semibold tracking-tight" data-testid="visuals-title">Visuals</h1>
      
      <Card className="sg-frame">
        <CardHeader><CardTitle>Live Audio Visualizer</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs">Pick local audio</label>
              <Input type="file" accept="audio/*" onChange={e=>{ const f=e.target.files?.[0]; if(f){ setAudioSrc(URL.createObjectURL(f)); } }} data-testid="visuals-audio-input"/>
            </div>
            <div>
              <label className="text-xs">Pick background image</label>
              <Input type="file" accept="image/*" onChange={e=>{ const f=e.target.files?.[0]; if(f){ setImg(URL.createObjectURL(f)); } }} data-testid="visuals-image-input"/>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={start} data-testid="visuals-start">Start</Button>
            <Button variant="secondary" onClick={stop} data-testid="visuals-stop">Stop</Button>
          </div>
          <audio src={audioSrc} ref={audioRef} className="hidden" />
          <div className="w-full border rounded overflow-hidden" style={{height:480}}>
            <canvas ref={canvasRef} className="w-full" data-testid="visuals-canvas"/>
          </div>
        </CardContent>
      </Card>

      <Separator />

      <Card className="sg-frame">
        <CardHeader><CardTitle>Protocol-Specific Visuals</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Select Protocol/Mood Stack</Label>
            <Select onValueChange={v => setSelectedProtocol(v)} defaultValue={selectedProtocol}>
              <SelectTrigger data-testid="protocol-select">
                <SelectValue placeholder="Select protocol" />
              </SelectTrigger>
              <SelectContent>
                {PROTOCOLS.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border rounded p-4 space-y-3">
            <h3 className="font-medium text-sm">Upload Images for {selectedProtocol}</h3>
            <Input 
              type="file" 
              accept="image/*" 
              onChange={e => uploadProtocolImage(e.target.files?.[0])} 
              disabled={uploadingImage}
              data-testid="protocol-image-upload"
            />
            {currentVisual?.image_urls && currentVisual.image_urls.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {currentVisual.image_urls.map((url, idx) => (
                  <img key={`visual-${url.slice(-12)}-${idx}`} src={url} alt={`${selectedProtocol} visual ${idx}`} className="w-full h-24 object-cover rounded border" />
                ))}
              </div>
            )}
          </div>

          <div className="border rounded p-4 space-y-3">
            <h3 className="font-medium text-sm">Touch Designer Video</h3>
            <Input 
              type="file" 
              accept="video/*" 
              onChange={e => uploadTDVideo(e.target.files?.[0])} 
              disabled={uploadingImage}
              data-testid="td-video-upload"
            />
            {currentVisual?.td_video_url && (
              <video src={currentVisual.td_video_url} controls className="w-full rounded border" />
            )}
          </div>

          <div className="border rounded p-4 space-y-3">
            <h3 className="font-medium text-sm">Touch Designer Stream URL</h3>
            <div className="flex gap-2">
              <Input 
                placeholder="https://your-td-stream-url.com" 
                value={tdStreamUrl}
                onChange={e => setTdStreamUrl(e.target.value)}
                data-testid="td-stream-input"
              />
              <Button onClick={saveTDStreamUrl} size="sm">Save</Button>
            </div>
            {currentVisual?.td_stream_url && (
              <div className="text-xs text-muted-foreground">
                Current: <a href={currentVisual.td_stream_url} target="_blank" rel="noopener noreferrer" className="underline">{currentVisual.td_stream_url}</a>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
